// Singleton adaptador de Keycloak para el SPA.
//
// Modo de auth controlado por `VITE_AUTH_MODE`:
//   - `legacy`   (default): no se carga Keycloak; se usa el JWT HS256 emitido
//                por auth-tenant-svc (camino actual mantenido para no romper
//                instalaciones en producción mientras se migra).
//   - `keycloak`           : se inicializa keycloak-js con PKCE S256, login
//                redirect, refresh silencioso (`updateToken`) y rotación de
//                refresh-token gestionada por Keycloak.
//
// Variables de entorno (definir en `frontend/.env.local` o en wrangler vars):
//   VITE_AUTH_MODE=keycloak
//   VITE_KEYCLOAK_URL=https://auth.toscana-clinica.com/auth
//   VITE_KEYCLOAK_REALM=toscana-clinica
//   VITE_KEYCLOAK_CLIENT_ID=toscana-clinica-spa
//
// Diseño de auto-refresh:
//   - `refreshIfNeeded(30)` se llama antes de CADA request HTTP en api.ts:
//     pide a Keycloak renovar si quedan <30s. Esto evita que el usuario vea
//     un 401 por token vencido sin necesidad de recargar la página.
//   - Además, `scheduleProactiveRefresh()` programa un setTimeout que renueva
//     el token cuando le quedan ~60s, garantizando que aunque el usuario esté
//     inactivo (sin requests) la sesión se mantenga viva durante los 24h del
//     refresh-token con rotación.
//   - Si la renovación falla (refresh token expirado / revocado), se llama
//     `kc.login()` para re-autenticar.

import Keycloak from 'keycloak-js';

const AUTH_MODE = (import.meta.env.VITE_AUTH_MODE ?? 'legacy') as 'legacy' | 'keycloak';

export function isKeycloakMode(): boolean {
  return AUTH_MODE === 'keycloak';
}

let kc: Keycloak | null = null;
let initPromise: Promise<boolean> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

const KC_SESSION_KEY = 'toscana.kc.session.v1';

type StoredKcSession = {
  token: string;
  refreshToken?: string;
  idToken?: string;
  expiresAt: number;
};


function persistKcSession(): void {
  if (typeof localStorage === 'undefined') return;
  const instance = keycloak();
  if (!instance.authenticated || !instance.token || !instance.tokenParsed?.exp) {
    localStorage.removeItem(KC_SESSION_KEY);
    return;
  }
  const payload: StoredKcSession = {
    token: instance.token,
    refreshToken: instance.refreshToken,
    idToken: instance.idToken,
    expiresAt: instance.tokenParsed.exp * 1000,
  };
  localStorage.setItem(KC_SESSION_KEY, JSON.stringify(payload));
}

/** Restaura sesión del login embebido (password grant). No usa init() de keycloak-js. */
export function restoreKcSession(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    const raw = localStorage.getItem(KC_SESSION_KEY);
    if (!raw) return false;
    const stored = JSON.parse(raw) as StoredKcSession;
    if (!stored.token) return false;

    const instance = keycloak();
    instance.token = stored.token;
    instance.refreshToken = stored.refreshToken;
    instance.idToken = stored.idToken;
    instance.tokenParsed = parseJwt(stored.token) as typeof instance.tokenParsed;
    if (instance.refreshToken) {
      instance.refreshTokenParsed = parseJwt(instance.refreshToken) as typeof instance.refreshTokenParsed;
    }
    const nowSec = Math.floor(Date.now() / 1000);
    instance.timeSkew = nowSec - ((instance.tokenParsed?.iat as number | undefined) ?? nowSec);
    instance.authenticated = true;
    scheduleProactiveRefresh();
    return true;
  } catch {
    localStorage.removeItem(KC_SESSION_KEY);
    return false;
  }
}

export function clearKcSession(): void {
  if (typeof localStorage !== 'undefined') localStorage.removeItem(KC_SESSION_KEY);
  clearRefreshTimer();
  const instance = keycloak();
  instance.authenticated = false;
  instance.token = undefined;
  instance.refreshToken = undefined;
  instance.idToken = undefined;
}

/** Devuelve la instancia singleton (sólo válida si {@link isKeycloakMode}). */
export function keycloak(): Keycloak {
  if (!kc) {
    kc = new Keycloak({
      url:      import.meta.env.VITE_KEYCLOAK_URL      ?? 'https://auth.toscana-clinica.com/auth',
      realm:    import.meta.env.VITE_KEYCLOAK_REALM    ?? 'toscana-salud',
      clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? 'toscana-clinica-spa',
    });
  }
  return kc;
}

/**
 * Inicializa Keycloak en silent SSO. Idempotente: llamadas posteriores
 * devuelven la misma promesa. Debe invocarse al bootstrap del SPA antes
 * de renderizar componentes autenticados.
 *
 * Devuelve `true` si hay sesión activa, `false` si no (en cuyo caso el
 * llamador puede mostrar el botón "Iniciar sesión" que invoca {@link login}).
 */
export function initKeycloak(): Promise<boolean> {
  if (!isKeycloakMode()) return Promise.resolve(false);
  if (initPromise) return initPromise;

  const instance = keycloak();
  initPromise = Promise.race<boolean>([
    instance
      .init({
        pkceMethod: 'S256',
        checkLoginIframe: false,
        enableLogging: import.meta.env.DEV,
      })
      .then((authenticated) => {
        if (authenticated) {
          scheduleProactiveRefresh();
          persistKcSession();
        }
        instance.onAuthSuccess = () => { scheduleProactiveRefresh(); persistKcSession(); };
        instance.onAuthRefreshSuccess = () => { scheduleProactiveRefresh(); persistKcSession(); };
        instance.onAuthLogout = () => { clearKcSession(); };
        instance.onTokenExpired = () => { void refreshIfNeeded(0); };
        return authenticated;
      })
      .catch((err) => {
        console.error('[keycloak] init falló', err);
        return false;
      }),
    new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 4_000)),
  ]);
  return initPromise;
}


/** @deprecated No redirige a Keycloak; use loginWithPassword vía auth.svelte login(). */
export function login(_redirectUri?: string): Promise<void> {
  return Promise.reject(new Error('Use login(usuario, contraseña) en la app.'));
}

/** Cierra sesión local (sin redirect a Keycloak). */
export function logout(_redirectUri?: string): Promise<void> {
  clearKcSession();
  return Promise.resolve();
}

function parseJwt(token: string): Record<string, unknown> {
  const payload = token.split('.')[1];
  const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
  return JSON.parse(json) as Record<string, unknown>;
}

function tokenEndpoint(): string {
  const realm = import.meta.env.VITE_KEYCLOAK_REALM ?? 'toscana-salud';
  // Mismo origen: el Worker proxya /auth/* al tunnel (sin CORS ni redirect).
  if (typeof window !== 'undefined') {
    return `/auth/realms/${realm}/protocol/openid-connect/token`;
  }
  const base = import.meta.env.VITE_KEYCLOAK_URL ?? 'https://auth.toscana-clinica.com/auth';
  return `${base}/realms/${realm}/protocol/openid-connect/token`;
}

async function postToken(body: URLSearchParams, timeoutMs = 8_000): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(tokenEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
      body,
      signal: ctrl.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

export type PasswordLoginResult = { ok: true } | { ok: false; error: string };

/**
 * Login directo en el SPA (sin redirect a la pantalla de Keycloak).
 * Requiere directAccessGrantsEnabled en el cliente publico toscana-clinica-spa.
 */
export async function loginWithPassword(username: string, password: string): Promise<PasswordLoginResult> {
  const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? 'toscana-clinica-spa';
  const body = new URLSearchParams({
    grant_type: 'password',
    client_id: clientId,
    username: username.trim(),
    password: password.trim(),
    scope: 'openid',
  });

  const r = await postToken(body);

  const raw = await r.text();
  let data: Record<string, unknown> = {};
  try { data = JSON.parse(raw) as Record<string, unknown>; } catch { /* noop */ }

  if (!r.ok) {
    const code = (data.error ?? '').toString();
    const desc = (data.error_description ?? data.error ?? raw).toString();
    if (code === 'invalid_grant' || /invalid user credentials/i.test(desc)) {
      return {
        ok: false,
        error: 'Usuario o contraseña incorrectos. Use su correo @toscanasystems.com (no su Gmail personal).',
      };
    }
    if (/not fully set up/i.test(desc)) {
      return {
        ok: false,
        error: 'La cuenta aún no está activada. Use el usuario @toscanasystems.com y la contraseña temporal del registro. Si persiste, contacte soporte al WhatsApp 3786-3061.',
      };
    }
    return { ok: false, error: desc || `HTTP ${r.status}` };
  }

  const access = data.access_token as string | undefined;
  if (!access) return { ok: false, error: 'Token vacío' };

  const instance = keycloak();
  instance.token = access;
  instance.refreshToken = (data.refresh_token as string | undefined) ?? undefined;
  instance.idToken = (data.id_token as string | undefined) ?? undefined;
  instance.tokenParsed = parseJwt(access) as typeof instance.tokenParsed;
  if (instance.refreshToken) {
    instance.refreshTokenParsed = parseJwt(instance.refreshToken) as typeof instance.refreshTokenParsed;
  }
  instance.authenticated = true;
  const nowSec = Math.floor(Date.now() / 1000);
  instance.timeSkew = nowSec - ((instance.tokenParsed?.iat as number | undefined) ?? nowSec);
  scheduleProactiveRefresh();
  persistKcSession();
  return { ok: true };
}

/**
 * Renueva el access token si le quedan menos de `minValiditySeconds` segundos.
 * Si el refresh-token está expirado/revocado, dispara `kc.login()`.
 *
 * Llamar antes de cada request HTTP (ver api.ts).
 */
/** Refresh manual vía mismo origen (/auth/* proxy) — evita 400 en bucle de keycloak-js. */
async function refreshAccessToken(): Promise<boolean> {
  const instance = keycloak();
  const refresh = instance.refreshToken;
  if (!refresh) return false;

  const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? 'toscana-clinica-spa';
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: clientId,
    refresh_token: refresh,
  });

  const r = await postToken(body);

  const raw = await r.text();
  let data: Record<string, unknown> = {};
  try { data = JSON.parse(raw) as Record<string, unknown>; } catch { /* noop */ }

  if (!r.ok) {
    console.warn('[keycloak] refresh falló', data.error ?? raw);
    return false;
  }

  const access = data.access_token as string | undefined;
  if (!access) return false;

  instance.token = access;
  instance.refreshToken = (data.refresh_token as string | undefined) ?? refresh;
  instance.idToken = (data.id_token as string | undefined) ?? instance.idToken;
  instance.tokenParsed = parseJwt(access) as typeof instance.tokenParsed;
  if (instance.refreshToken) {
    instance.refreshTokenParsed = parseJwt(instance.refreshToken) as typeof instance.refreshTokenParsed;
  }
  instance.authenticated = true;
  persistKcSession();
  return true;
}

export async function refreshIfNeeded(minValiditySeconds = 30): Promise<string | null> {
  if (!isKeycloakMode()) return null;
  const instance = keycloak();
  if (!instance.authenticated || !instance.tokenParsed?.exp) return null;

  const expiresIn = instance.tokenParsed.exp - Math.floor(Date.now() / 1000);
  if (expiresIn >= minValiditySeconds) return instance.token ?? null;

  const ok = await refreshAccessToken();
  if (!ok) {
    clearRefreshTimer();
    instance.authenticated = false;
    instance.token = undefined;
    instance.refreshToken = undefined;
    instance.idToken = undefined;
    clearKcSession();
    return null;
  }
  persistKcSession();
  scheduleProactiveRefresh();
  return instance.token ?? null;
}

/** Programa un timer para renovar antes del expiry (proactivo). */
function scheduleProactiveRefresh() {
  clearRefreshTimer();
  const instance = keycloak();
  if (!instance.authenticated || !instance.tokenParsed?.exp) return;
  const expMs = instance.tokenParsed.exp * 1000;
  // Renovar 60s antes; mínimo 5s para evitar loops.
  const delay = Math.max(expMs - Date.now() - 60_000, 5_000);
  refreshTimer = setTimeout(() => { void refreshIfNeeded(60); }, delay);
}

function clearRefreshTimer() {
  if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
}

/** Lee el claim `tenantId` del access token actual (null si no hay sesión). */
export function currentTenantId(): string | null {
  const t = keycloak().tokenParsed as { tenantId?: string } | undefined;
  return t?.tenantId ?? null;
}

/** Lee el username (`preferred_username`) del token actual. */
export function currentUsername(): string | null {
  const t = keycloak().tokenParsed as { preferred_username?: string } | undefined;
  return t?.preferred_username ?? null;
}

/** Lista de roles realm del usuario actual. */
export function currentRoles(): string[] {
  const t = keycloak().tokenParsed as { realm_access?: { roles?: string[] } } | undefined;
  return t?.realm_access?.roles ?? [];
}
